/**
 * London Smoke & Cure — admin desktop notifications: calendar day + optional daily time window.
 * All times use Europe/London (business timezone).
 */

export const DESKTOP_NOTIFY_TIMEZONE = 'Europe/London';

const dtfDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: DESKTOP_NOTIFY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const dtfParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: DESKTOP_NOTIFY_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

/**
 * @param {number} ms
 * @returns {string} YYYY-MM-DD in London
 */
export function getCalendarDateKeyLondon(ms) {
    return dtfDate.format(new Date(ms));
}

/**
 * True when `placedMs` falls on the same calendar day as "now" (London).
 * @param {number} placedMs
 * @param {number} [nowMs=Date.now()]
 */
export function isSameCalendarDayLondon(placedMs, nowMs = Date.now()) {
    return getCalendarDateKeyLondon(placedMs) === getCalendarDateKeyLondon(nowMs);
}

/**
 * First UTC millisecond of the Europe/London calendar day that contains `instantMs`.
 * Used for chart "today" markers (walk then step back; safe across BST/GMT).
 *
 * @param {number} [instantMs=Date.now()]
 * @returns {number}
 */
export function startOfCalendarDayLondonMs(instantMs = Date.now()) {
    const dayStr = getCalendarDateKeyLondon(instantMs);
    let t = instantMs - 45 * 3600000;
    const hi = instantMs + 26 * 3600000;
    let guard = 0;
    while (t < hi && getCalendarDateKeyLondon(t) !== dayStr && guard < 200) {
        t += 30 * 60000;
        guard += 1;
    }
    if (getCalendarDateKeyLondon(t) !== dayStr) {
        return instantMs;
    }
    while (t > instantMs - 50 * 3600000 && getCalendarDateKeyLondon(t - 60000) === dayStr) {
        t -= 60000;
    }
    let fineSteps = 0;
    while (t > instantMs - 50 * 3600000 && getCalendarDateKeyLondon(t - 1) === dayStr && fineSteps < 120000) {
        t -= 1;
        fineSteps += 1;
    }
    return t;
}

/**
 * Minute of day 0–1439 in London for the given instant.
 * @param {number} ms
 */
export function getMinuteOfDayLondon(ms) {
    const parts = dtfParts.formatToParts(new Date(ms));
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    return hour * 60 + minute;
}

/**
 * @param {number} minutes 0–1439
 * @returns {string} HH:mm for `<input type="time" step="60">`
 */
export function minutesToTimeInputValue(minutes) {
    const m = Math.max(0, Math.min(1439, Math.floor(minutes)));
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * @param {string} hhmm from `<input type="time">`
 * @returns {number|null} minutes 0–1439
 */
export function timeInputValueToMinutes(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const [h, min] = hhmm.split(':').map((x) => Number(x));
    if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
    const total = h * 60 + min;
    if (total < 0 || total > 1439) return null;
    return total;
}

/**
 * When `desktopNotifyHoursEnabled` is true, only allow notifications if current London time
 * falls inside [start, end) for same-day windows, or overnight when start > end.
 * When disabled or misconfigured, returns true (no hour restriction).
 *
 * @param {Record<string, unknown> | null | undefined} userData — Firestore `users/{uid}`
 * @param {number} [nowMs=Date.now()]
 */
export function isNowWithinDesktopNotifyHours(userData, nowMs = Date.now()) {
    if (!userData || typeof userData !== 'object') return true;
    if (userData.desktopNotifyHoursEnabled !== true) return true;

    const start = userData.desktopNotifyHoursStartMinute;
    const end = userData.desktopNotifyHoursEndMinute;
    if (typeof start !== 'number' || typeof end !== 'number') {
        return true;
    }
    if (start < 0 || start > 1439 || end < 0 || end > 1439) {
        return true;
    }
    if (start === end) return true;

    const m = getMinuteOfDayLondon(nowMs);
    if (start < end) {
        return m >= start && m < end;
    }
    return m >= start || m < end;
}
